/**
 * Opponent presence during a game: "Alex disconnected — waiting for them to
 * return…" with a live countdown until the win can be claimed
 * (`claimWinAvailableAt`, server clock), then a Claim win button.
 */
import { useState } from 'react';
import type { RoomView } from '@wordloom/engine';
import { claimWin } from '../game/actions';
import { opponentSeat } from '../game/events';
import { useServerNow } from '../hooks/useNow';
import { formatClock } from '../lib/time';
import { IconCrown, IconWifiOff } from './Icons';
import { Tooltip } from './Tooltip';
import './Banners.css';

export function PresenceBanner({ view }: { view: RoomView }) {
  const opponent = view.seats[opponentSeat(view)];
  const away = view.game?.status === 'playing' && opponent != null && !opponent.connected;
  const now = useServerNow(away, 1000);
  const [claiming, setClaiming] = useState(false);
  if (!away || !opponent) return null;

  const claimAt = view.claimWinAvailableAt;
  const remaining = claimAt != null ? claimAt - now : null;
  const canClaim = remaining != null && remaining <= 0;

  const onClaim = async () => {
    if (claiming) return;
    setClaiming(true);
    await claimWin();
    setClaiming(false);
  };

  return (
    <div className="presence-banner" role="status">
      <span className="presence-banner__icon" aria-hidden="true">
        <IconWifiOff />
      </span>
      <p className="presence-banner__text">
        <strong>{opponent.name} disconnected</strong>
        <span> — waiting for them to return…</span>
        {remaining != null && !canClaim && (
          <span className="presence-banner__count">
            {' '}
            You can claim the win in <span className="tabular">{formatClock(remaining)}</span>.
          </span>
        )}
      </p>
      {canClaim && (
        <Tooltip content={`End the game now as a win — ${opponent.name} has been away too long`}>
          <button
            type="button"
            className="btn btn--gold btn--sm presence-banner__claim"
            aria-disabled={claiming || undefined}
            onClick={() => void onClaim()}
          >
            {claiming ? <span className="btn__spinner" aria-hidden="true" /> : <IconCrown />}
            Claim win
          </button>
        </Tooltip>
      )}
    </div>
  );
}
